import { inject, Injectable } from '@angular/core';
import { RootServicesModule } from './root-services.module';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@app-environment';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from './translation.service';
import { TranslationData } from '../interfaces/translation.interface';
import { InformationType, IProfile } from '@scf-monorepo/interfaces';
import { SignedURLDto } from '@scf-monorepo/backend-lib/dtos';
import { downloadBlob } from '../../shared/helper/download.helper';

interface InformationData {
    language: string;
    [key: string]: string;
}

@Injectable({
    providedIn: RootServicesModule
})
export class InformationService {
    private readonly standardLanguage = 'en';

    private readonly http = inject(HttpClient);
    private readonly translate = inject(TranslateService);
    private readonly translationService = inject(TranslationService);

    getLegals(profile: IProfile, language?: string): Observable<any> {
        return this.queryLegals(profile).pipe(
            map((data) => this.filterForLanguage(data, language)),
            map((data) => ({
                ...data,
                organizationinformation: data.legalInformation || '',
                organizationname: data.company || '',
                companysign: data.companySign || '',
                OrgInternet: data.internet || ''
            })),
            catchError(() => {
                console.error(`Couldn't get legals-data`);
                return of({});
            })
        );
    }

    getManagement(profile: IProfile, language?: string): Observable<any> {
        return this.queryManagements(profile).pipe(
            map((data) => this.filterForLanguage(data, language)),
            map((data) => ({
                ...data,
                organizationname: data.organisation || '',
                OrgCode: data.orgUnit || '',
                management_line: data.managementLine || '',
                Org_Level1: data.orgLevel1 || '',
                Org_Level2: data.orgLevel2 || '',
                Org_LevelRest: data.orgLevelRest || '',
                organizationmanagement: data.managementLine || ''
            })),
            catchError(() => {
                console.error(`Couldn't get management-data`);
                return of({});
            })
        );
    }

    getLocations(profile: IProfile, language?: string): Observable<any> {
        return this.queryLocations(profile).pipe(
            map((data) => this.filterForLanguage(data, language)),
            map((data) => ({
                ...data,
                locationcontact: data.locationContact || '',
                locationaddress: data.locationAddress || '',
                senderaddress: data.senderAddress || '',
                Street: data.street || '',
                Street2: data.street2 || '',
                StreetPostalCode: data.streetPostalCode || '',
                StreetLocality: data.streetLocality || '',
                PhoneNumberExchange: data.phoneNumberExchange || '',
                FaxNumberExchange: data.faxNumberExchange || ''
            })),
            catchError(() => {
                console.error(`Couldn't get locations-data`);
                return of({});
            })
        );
    }

    public download(type: InformationType): Observable<Blob> {
        return this.http.get<SignedURLDto>(`${environment.gatewayAPI}/information/${type}/download`).pipe(
            switchMap(({ signedURL }) => this.http.get(signedURL, { responseType: 'blob' })),
            tap((blob) => {
                downloadBlob(blob, `${type}.csv`);
            })
        );
    }

    private getTranslations(language: string): Observable<TranslationData> {
        const defaultLang = 'en';

        const langRequest = this.translationService.getLanguage(language).pipe(catchError(() => of({})));

        const defaultLangRequest = this.translationService.getLanguage(defaultLang).pipe(catchError(() => of({})));

        return forkJoin([defaultLangRequest, langRequest]).pipe(
            map(([defaultLangTranslations, langTranslations]) => {
                const combinedTranslations = { ...defaultLangTranslations, ...langTranslations };

                combinedTranslations.legal_information = combinedTranslations.legalInformation || '';
                combinedTranslations.company_sign = combinedTranslations.companySign || '';
                combinedTranslations.Internet = combinedTranslations.internet || '';

                return combinedTranslations;
            }),
            catchError(() => of({}))
        );
    }

    getAllInformations(
        language: string,
        profile: IProfile
    ): Observable<{
        [key: string]: string | Array<string>;
    }> {
        return forkJoin([
            this.getTranslations(language),
            this.getManagement(profile, language),
            this.getLegals(profile, language),
            this.getLocations(profile, language)
        ]).pipe(
            map(([translations, managements, legals, locations]) => ({
                ...translations,
                ...managements,
                ...legals,
                ...locations
            })),
            map((data) => this.convertNonBreakingSpaces(data)),
            map((data) => this.convertLineBreak(data)),
            map((data) => this.convertPipes(data))
        );
    }

    private convertNonBreakingSpaces(data: any) {
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key) && typeof data[key] === 'string') {
                data[key] = data[key].split('~').join('\xA0');
            }
        }
        return data;
    }

    // Convert all (#10) to line breaks
    private convertLineBreak(data: any) {
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key) && typeof data[key] === 'string') {
                data[key] = data[key].split('(#10)').join('\n');
            }
        }
        return data;
    }

    // Convert all information values to arrays
    private convertPipes(data: any) {
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key) && typeof data[key] === 'string') {
                data[key] = data[key].split('|').map((val: string) => val.trim());
            }
        }
        return data;
    }

    checkProfileInformation(profile: IProfile) {
        return forkJoin([this.queryLegals(profile), this.queryManagements(profile), this.queryLocations(profile)]).pipe(
            catchError(() => {
                return throwError(this.translate.instant('popup-coreinformation-could-not-load'));
            }),
            switchMap(([legals, managements, locations]) => {
                if (legals.length && managements.length && locations.length) {
                    return of({
                        legals,
                        managements,
                        locations
                    });
                }

                const legalsError = legals.length === 0 ? 'popup-coreinformation-missing-legal' : '';
                const managementError = managements.length === 0 ? 'popup-coreinformation-missing-management' : '';
                const locationError = locations.length === 0 ? 'popup-coreinformation-missing-location' : '';

                return throwError(this.getInformationErrorMessage([legalsError, managementError, locationError]));
            })
        );
    }

    private getInformationErrorMessage(errors: string[]) {
        return () => {
            const errorInfo = this.translate.instant('popup-coreinformation-missing');
            const errorMessage = errors
                .filter((value) => value)
                .map((value) => this.translate.instant(value))
                .join(', ');
            return `${errorInfo}: \n${errorMessage}`;
        };
    }

    private queryLegals({ organization, country, department }: IProfile): Observable<InformationData[]> {
        let params = new HttpParams();
        if (country) {
            params = params.append('country', country);
        }
        if (department) {
            params = params.append('orgUnit', department);
        }
        return this.http.get<Array<InformationData>>(
            `${environment.gatewayAPI}/information/${InformationType.Legal}/${organization.toUpperCase()}`,
            {
                params
            }
        );
    }

    private queryManagements({ organization, country, department }: IProfile): Observable<InformationData[]> {
        let params = new HttpParams();
        params = params.append('orgUnit', department);
        if (country) {
            params = params.append('country', country);
        }
        return this.http.get<Array<InformationData>>(
            `${environment.gatewayAPI}/information/${InformationType.Management}/${organization.toUpperCase()}`,
            {
                params
            }
        );
    }

    private queryLocations({ organization, location, country, department }: IProfile): Observable<InformationData[]> {
        let params = new HttpParams();
        params = params.append('location', location);
        if (country) {
            params = params.append('country', country);
        }
        if (department) {
            params = params.append('orgUnit', department);
        }
        return this.http.get<Array<InformationData>>(
            `${environment.gatewayAPI}/information/${InformationType.Location}/${organization.toUpperCase()}`,
            {
                params
            }
        );
    }

    private filterForLanguage(data: InformationData[], language?: string): InformationData {
        if (!language) {
            return data[0] || ({} as InformationData);
        }

        let langData = data.find((item) => item.language.toLowerCase() === language.toLowerCase());
        if (!langData) {
            langData = data.find((item) => item.language.toLowerCase() === this.standardLanguage);
        }
        return langData || ({} as InformationData);
    }
}
